import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ShippingMethodConfig } from '@/models/orgaConfiguration';
import { SendCloudShippingMethod } from '@/models/sendcloud';
import { Search, Package, MapPin, Check, Plus, X, ChevronDown, ChevronUp, Globe, AlertTriangle } from 'lucide-react';
import { ShippingService } from '@/services/shipping/shipping-service';
import { applySendcloudMarkup, sendcloudCountryCode } from '@/shared/utils/sendcloud-shipping';

const COUNTRY_NAMES: Record<string, string> = {
  FR: 'France',
  BE: 'Belgique',
  CH: 'Suisse',
  DE: 'Allemagne',
  ES: 'Espagne',
  IT: 'Italie',
  NL: 'Pays-Bas',
  GB: 'Royaume-Uni',
  PT: 'Portugal',
  AT: 'Autriche',
  LU: 'Luxembourg',
  IE: 'Irlande',
  DK: 'Danemark',
  SE: 'Suède',
  NO: 'Norvège',
  FI: 'Finlande',
  PL: 'Pologne',
  CZ: 'République tchèque',
  HU: 'Hongrie',
  SK: 'Slovaquie',
  SI: 'Slovénie',
  HR: 'Croatie',
  RO: 'Roumanie',
  BG: 'Bulgarie',
  GR: 'Grèce',
  CY: 'Chypre',
  MT: 'Malte',
  EE: 'Estonie',
  LV: 'Lettonie',
  LT: 'Lituanie',
};

interface CarrierFinderProps {
  onAddMethod?: (method: ShippingMethodConfig) => Promise<void> | void;
  /** IDs Sendcloud déjà enregistrés (sendcloudMethodId) */
  alreadyAddedIds?: number[];
  /** Catalogue plateforme vs compte marchand (clés orga) */
  catalogMode?: 'platform' | 'merchant';
  /** Requis si catalogMode === 'merchant' */
  organizationId?: string;
}

interface FilterCriteria {
  minPrice: number;
  maxPrice: number;
  countries: string[];
  minWeight: number;
  maxWeight: number;
  serviceType: 'all' | 'home' | 'pickup';
  searchQuery: string;
}

const ITEMS_PER_PAGE = 15;

const CarrierFinder: React.FC<CarrierFinderProps> = ({
  onAddMethod,
  alreadyAddedIds = [],
  catalogMode = 'platform',
  organizationId,
}) => {
  const { t } = useTranslation();

  // Fetch Sendcloud methods on demand (no global context needed)
  const [allMethods, setAllMethods] = useState<SendCloudShippingMethod[]>([]);
  const [sendCloudLoading, setSendCloudLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const contextInitialLoading = sendCloudLoading;

  useEffect(() => {
    setSendCloudLoading(true);
    setLoadError(null);
    const load =
      catalogMode === 'merchant' && organizationId
        ? ShippingService.browseSendcloudMethodsMerchant(organizationId)
        : ShippingService.browseSendcloudMethods();
    load
      .then(methods => {
        setAllMethods(Array.isArray(methods) ? methods : []);
        setLoadError(null);
      })
      .catch(err => {
        console.error('Erreur chargement méthodes Sendcloud:', err);
        setAllMethods([]);
        const message = err instanceof Error ? err.message : String(err);
        setLoadError(message || t('admin.configuration.shipping.search.loadError'));
      })
      .finally(() => setSendCloudLoading(false));
  }, [catalogMode, organizationId, t]);

  const [currentPage, setCurrentPage] = useState(1);
  const [expandedMethod, setExpandedMethod] = useState<number | null>(null);
  const [isCountrySelectOpen, setIsCountrySelectOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [showLoader, setShowLoader] = useState(true);

  const [filters, setFilters] = useState<FilterCriteria>({
    minPrice: 0,
    maxPrice: 50,
    countries: [],
    minWeight: 0,
    maxWeight: 30,
    serviceType: 'all',
    searchQuery: '',
  });
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] = useState(false);
  const [customizationModal, setCustomizationModal] = useState<{
    method: SendCloudShippingMethod | null;
    customName: string;
    countryPrices: Record<string, number>;
    selectedCountries: string[];
  }>({
    method: null,
    customName: '',
    countryPrices: {},
    selectedCountries: [],
  });
  const [modalError, setModalError] = useState<string | null>(null);
  const isCustomizationConfirmDisabled = useMemo(() => {
    if (!customizationModal.method) return true;
    return customizationModal.selectedCountries.length === 0;
  }, [customizationModal]);

  // Fermer le dropdown des pays quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.country-select-container')) {
        setIsCountrySelectOpen(false);
      }
    };

    if (isCountrySelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCountrySelectOpen]);

  // Liste des pays européens populaires (fallback)
  const fallbackCountries = [
    { code: 'FR', name: COUNTRY_NAMES['FR'] || 'France' },
    { code: 'BE', name: COUNTRY_NAMES['BE'] || 'Belgium' },
    { code: 'DE', name: COUNTRY_NAMES['DE'] || 'Germany' },
    { code: 'ES', name: COUNTRY_NAMES['ES'] || 'Spain' },
    { code: 'IT', name: COUNTRY_NAMES['IT'] || 'Italy' },
    { code: 'NL', name: COUNTRY_NAMES['NL'] || 'Netherlands' },
    { code: 'PT', name: COUNTRY_NAMES['PT'] || 'Portugal' },
    { code: 'LU', name: COUNTRY_NAMES['LU'] || 'Luxembourg' },
  ];

  // Extraire les pays disponibles dynamiquement depuis les méthodes SendCloud
  const availableCountries = useMemo(() => {
    if (sendCloudLoading || allMethods.length === 0) return fallbackCountries;

    const countrySet = new Set<string>();
    const countryMap = new Map<string, string>();

    // Parcourir toutes les méthodes SendCloud pour extraire les pays où on livre
    // Exclure la méthode "unstamp letter" (ID 8) de l'extraction des pays
    for (const method of allMethods) {
      // Exclure la méthode ID 8 (unstamp letter) de l'extraction des pays
      if (method.id === 8) continue;

      if (method.countries && Array.isArray(method.countries)) {
        for (const country of method.countries) {
          const cc = sendcloudCountryCode(country as { code?: string; iso_2?: string });
          if (cc) {
            countrySet.add(cc);
            if (country.name && String(country.name).trim()) {
              countryMap.set(cc, String(country.name));
            }
          }
        }
      }
    }

    // Convertir en tableau avec noms, triés par code
    const countries = Array.from(countrySet)
      .sort() // Trier par code de pays
      .map(code => ({
        code,
        name: COUNTRY_NAMES[code] || countryMap.get(code) || code
      }));

    // Retourner les pays trouvés ou le fallback si aucun pays n'est disponible
    return countries.length > 0 ? countries : fallbackCountries;
  }, [allMethods, sendCloudLoading]);

  // Filtrer les pays par recherche
  const filteredCountries = useMemo(() => {
    if (!countrySearchQuery) return availableCountries;
    return availableCountries.filter(country =>
      country.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) ||
      country.code.toLowerCase().includes(countrySearchQuery.toLowerCase())
    );
  }, [availableCountries, countrySearchQuery]);

  // Obtenir les pays sélectionnés
  const selectedCountries = useMemo(() => {
    return availableCountries.filter(country =>
      filters.countries.includes(country.code)
    );
  }, [availableCountries, filters.countries]);

  // Gérer l'affichage du loader avec un délai minimum pour éviter le clignement
  useEffect(() => {
    if (contextInitialLoading) {
      setShowLoader(true);
    } else {
      const timer = setTimeout(() => {
        setShowLoader(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [contextInitialLoading]);

  // Réinitialiser la page à 1 quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Mémoriser les IDs des méthodes sélectionnées pour éviter les re-renders
  const selectedMethodIds = useMemo(
    () => Array.from(new Set(alreadyAddedIds)),
    [alreadyAddedIds],
  );

  // Filtrer et trier les méthodes (ultra-optimisé)
  const filteredMethods = useMemo(() => {
    // Protection contre les rechargements inutiles
    if (sendCloudLoading || allMethods.length === 0) return [];

    // Filtrage silencieux et rapide avec IDs mémorisés
    const filtered = allMethods.filter(method => {
      // Exclure la méthode "unstamp letter" (ID 8)
      if (method.id === 8) return false;

      // Filtrer par poids
      const methodMinWeight = Number(method.minWeight) || 0;
      const methodMaxWeight = Number(method.maxWeight) || 0;

      if (methodMinWeight > filters.maxWeight || methodMaxWeight < filters.minWeight) {
        return false;
      }

      // Filtrer par prix (le prix minimum et maximum sont calculés depuis les pays)
      if (method.countries && method.countries.length > 0) {
        const prices = method.countries.map(c => applySendcloudMarkup(c.price));
        const methodMinPrice = Math.min(...prices);
        const methodMaxPrice = Math.max(...prices);

        if (methodMinPrice > filters.maxPrice || methodMaxPrice < filters.minPrice) {
          return false;
        }
      } else if (filters.minPrice > 0 || filters.maxPrice < 50) {
        // Si pas de pays mais qu'on filtre par prix, exclure la méthode
        return false;
      }

      // Filtrer par type de service
      if (filters.serviceType !== 'all') {
        if (filters.serviceType === 'home' && method.servicePointInput !== 'none') return false;
        if (filters.serviceType === 'pickup' && method.servicePointInput !== 'required') return false;
      }

      // Filtrer par pays
      if (filters.countries.length > 0) {
        const methodCountries =
          method.countries?.map(c => sendcloudCountryCode(c as { code?: string; iso_2?: string })).filter(Boolean) || [];
        const hasMatchingCountry = filters.countries.some(country =>
          methodCountries.includes(country)
        );
        if (!hasMatchingCountry) return false;
      }

      // Filtrer par recherche
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesName = method.name?.toLowerCase().includes(query);
        const matchesCarrier = method.carrier?.toLowerCase().includes(query);
        if (!matchesName && !matchesCarrier) return false;
      }

      return true;
    });

    return filtered;
  }, [allMethods, selectedMethodIds, filters, sendCloudLoading]);

  // Fonctions de gestion des filtres
  const handleToggleCountry = (countryCode: string) => {
    setFilters(prev => ({
      ...prev,
      countries: prev.countries.includes(countryCode)
        ? prev.countries.filter(c => c !== countryCode)
        : [...prev.countries, countryCode]
    }));
  };

  const resetFilters = () => {
    setFilters({
      minPrice: 0,
      maxPrice: 50,
      countries: [],
      minWeight: 0,
      maxWeight: 30,
      serviceType: 'all',
      searchQuery: '',
    });
    setCurrentPage(1);
  };

  const openCustomizationModal = (method: SendCloudShippingMethod) => {
    const prices: Record<string, number> = {};
    method.countries?.forEach(country => {
      const code = sendcloudCountryCode(country as { code?: string; iso_2?: string });
      if (code) {
        const markedPrice = applySendcloudMarkup(country.price);
        prices[code] = Math.max(0, markedPrice);
      }
    });

    setCustomizationModal({
      method,
      customName: method.name || '',
      countryPrices: prices,
      selectedCountries:
        method.countries?.map(c => sendcloudCountryCode(c as { code?: string; iso_2?: string })).filter(Boolean) || [],
    });
    setModalError(null);
    setIsCustomizationModalOpen(true);
  };

  const closeCustomizationModal = () => {
    setIsCustomizationModalOpen(false);
    setCustomizationModal({
      method: null,
      customName: '',
      countryPrices: {},
      selectedCountries: [],
    });
    setModalError(null);
  };

  const handlePriceChange = (countryCode: string, value: string) => {
    setCustomizationModal(prev => {
      if (!prev.method) return prev;
      const numericValue = Number(value);
      return {
        ...prev,
        countryPrices: {
          ...prev.countryPrices,
          [countryCode]: Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0,
        },
      };
    });
  };

  const handleToggleModalCountry = (countryCode: string) => {
    setCustomizationModal(prev => {
      if (!prev.method) return prev;
      const isSelected = prev.selectedCountries.includes(countryCode);

      if (isSelected) {
        if (prev.selectedCountries.length === 1) {
          setModalError(t('admin.configuration.shipping.customization.atLeastOneCountry'));
          return prev;
        }
        return {
          ...prev,
          selectedCountries: prev.selectedCountries.filter(code => code !== countryCode),
        };
      }

      return {
        ...prev,
        selectedCountries: [...prev.selectedCountries, countryCode],
      };
    });
  };

  const handleCustomizationSubmit = async () => {
    if (!customizationModal.method) return;
    const trimmedName = customizationModal.customName.trim();

    if (!trimmedName) {
      setModalError(t('admin.configuration.shipping.customization.nameError'));
      return;
    }

    if (customizationModal.selectedCountries.length === 0) {
      setModalError(t('admin.configuration.shipping.customization.atLeastOneCountry'));
      return;
    }

    const hasNegativePrice = customizationModal.selectedCountries.some(code => {
      const price = customizationModal.countryPrices[code];
      return price === undefined || price < 0 || Number.isNaN(price);
    });
    if (hasNegativePrice) {
      setModalError(t('admin.configuration.shipping.customization.priceError'));
      return;
    }

    try {
      const filteredPrices = customizationModal.selectedCountries.reduce<Record<string, number>>((acc, code) => {
        acc[code] = customizationModal.countryPrices[code] ?? 0;
        return acc;
      }, {});

      if (!onAddMethod) {
        setModalError(t('admin.configuration.shipping.customization.submitError'));
        return;
      }
      const m = customizationModal.method;
      const platformCountryPrices = customizationModal.selectedCountries.reduce<Record<string, number>>(
        (acc, code) => {
          const raw = m.countries?.find(
            c => sendcloudCountryCode(c as { code?: string; iso_2?: string }) === code,
          );
          const ref =
            raw != null
              ? catalogMode === 'merchant'
                ? raw.price
                : applySendcloudMarkup(raw.price)
              : (filteredPrices[code] ?? 0);
          acc[code] = ref;
          return acc;
        },
        {},
      );
      const newMethod: ShippingMethodConfig = {
        id: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        provider: catalogMode === 'merchant' ? 'sendcloud_api_key' : 'sendcloud',
        carrier: m.carrier || 'sendcloud',
        type: m.servicePointInput === 'none' ? 'home' : 'relay',
        weightRange: { min: m.minWeight ?? 0, max: m.maxWeight ?? 30 },
        availableCountries: customizationModal.selectedCountries,
        countryPrices: filteredPrices,
        platformCountryPrices,
        customName: trimmedName,
        isActive: true,
        sendcloudMethodId: m.id,
      };
      await onAddMethod(newMethod);
      closeCustomizationModal();
    } catch (error) {
      console.error('Erreur lors de la personnalisation de la méthode:', error);
      setModalError(t('admin.configuration.shipping.customization.submitError'));
    }
  };

  const selectAllModalCountries = useCallback(() => {
    setCustomizationModal(prev => {
      if (!prev.method?.countries) return prev;
      const allCountryCodes = prev.method.countries
        .map(country => sendcloudCountryCode(country as { code?: string; iso_2?: string }))
        .filter((code): code is string => Boolean(code));
      return {
        ...prev,
        selectedCountries: allCountryCodes,
      };
    });
    setModalError(null);
  }, []);

  const deselectAllModalCountries = useCallback(() => {
    setCustomizationModal(prev => {
      if (!prev.method) return prev;
      return {
        ...prev,
        selectedCountries: [],
      };
    });
    setModalError(null);
  }, []);

  // Pagination
  const totalPages = Math.ceil(filteredMethods.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedMethods = filteredMethods.slice(startIndex, endIndex);

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0 max-w-full">
      {/* En-tête modernisé */}
      <div className="bg-surface rounded-2xl shadow-sm p-4 sm:p-6 min-w-0 overflow-x-hidden">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-1">{t('admin.configuration.shipping.search.title')}</h3>
            <p className="text-sm text-foreground-secondary">{t('admin.configuration.shipping.search.subtitle')}</p>
          </div>
          <div className="px-4 py-2 bg-background-tertiary rounded-xl">
            <span className="text-sm font-medium text-foreground-secondary">{t('admin.configuration.shipping.search.results', { count: filteredMethods.length })}</span>
          </div>
        </div>

        {/* Barre de recherche moderne */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-foreground-secondary" />
            <input
              type="text"
              placeholder={t('admin.configuration.shipping.search.searchPlaceholder')}
              value={filters.searchQuery}
              onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
              className="w-full pl-12 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder:text-foreground-secondary text-sm text-foreground bg-surface"
            />
          </div>
        </div>

        {/* Filtres avancés modernisés */}
        <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 min-w-0">
            {/* Poids */}
            <div className="space-y-2 min-w-0">
              <label htmlFor="min-weight" className="block text-sm font-medium text-foreground mb-2">{t('admin.configuration.shipping.search.weightLabel')}</label>
              <div className="flex items-center gap-2">
                <input
                  id="min-weight"
                  type="number"
                  min="0"
                  step="0.1"
                  value={filters.minWeight}
                  onChange={(e) => setFilters(prev => ({ ...prev, minWeight: Number(e.target.value) }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-24 px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm text-foreground bg-surface"
                />
                <span className="text-foreground-secondary font-medium flex-shrink-0">-</span>
                <input
                  id="max-weight"
                  type="number"
                  min="0"
                  step="0.1"
                  value={filters.maxWeight}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxWeight: Number(e.target.value) }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-24 px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm text-foreground bg-surface"
                />
              </div>
            </div>

            {/* Prix */}
            <div className="space-y-2 min-w-0">
              <label htmlFor="min-price" className="block text-sm font-medium text-foreground mb-2">{t('admin.configuration.shipping.search.priceLabel')}</label>
              <div className="flex items-center gap-2">
                <input
                  id="min-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={filters.minPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, minPrice: Number(e.target.value) }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-24 px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm text-foreground bg-surface"
                />
                <span className="text-foreground-secondary font-medium flex-shrink-0">-</span>
                <input
                  id="max-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: Number(e.target.value) }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-24 px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm text-foreground bg-surface"
                />
              </div>
            </div>

            {/* Type de livraison */}
            <div className="min-w-0">
              <label htmlFor="delivery-type" className="block text-sm font-medium text-foreground mb-2">{t('admin.configuration.shipping.search.deliveryTypeLabel')}</label>
              <select
                id="delivery-type"
                value={filters.serviceType}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceType: e.target.value as 'all' | 'home' | 'pickup' }))}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all appearance-none cursor-pointer text-sm text-foreground bg-surface"
              >
                <option value="all">{t('admin.configuration.shipping.search.allTypes')}</option>
                <option value="home">{t('admin.configuration.shipping.search.home')}</option>
                <option value="pickup">{t('admin.configuration.shipping.search.pickup')}</option>
              </select>
            </div>

            {/* Pays - Select multi avec recherche directement intégré */}
            <div className="min-w-0 relative country-select-container">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('admin.configuration.shipping.search.countriesLabel')}
                <span className="text-xs text-foreground-secondary ml-1 font-normal"></span>
              </label>
              <button
                onClick={() => setIsCountrySelectOpen(!isCountrySelectOpen)}
                className="w-full px-4 py-3 text-left border border-border rounded-xl hover:border-border-medium focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-surface"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4 text-foreground-secondary" />
                    <span className="text-sm text-foreground-secondary font-medium truncate">
                      {selectedCountries.length === 0
                        ? t('admin.configuration.shipping.search.selectCountry')
                        : t('admin.configuration.shipping.search.countriesSelected', { count: selectedCountries.length })}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-foreground-secondary transition-transform flex-shrink-0 ${isCountrySelectOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* Dropdown avec recherche modernisé */}
              {isCountrySelectOpen && (
                <div className="absolute z-10 w-full mt-2 bg-surface rounded-xl shadow-xl border border-border max-h-64 overflow-hidden">
                  {/* Barre de recherche */}
                  <div className="p-3 border-b border-border bg-background-tertiary">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-secondary" />
                      <input
                        type="text"
                        placeholder={t('admin.configuration.shipping.search.searchCountry')}
                        value={countrySearchQuery}
                        onChange={(e) => setCountrySearchQuery(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-foreground"
                      />
                    </div>
                  </div>

                  {/* Liste des pays */}
                  <div className="max-h-48 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map(country => (
                        <button
                          key={country.code}
                          onClick={() => handleToggleCountry(country.code)}
                          className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between transition-colors ${filters.countries.includes(country.code)
                              ? 'bg-primary-10 hover:bg-primary-20'
                              : 'hover:bg-background-tertiary'
                            }`}
                        >
                          <span className="flex items-center space-x-2">
                            <span className="text-foreground font-medium">{country.name}</span>
                            <span className="text-xs text-foreground-secondary">({country.code})</span>
                          </span>
                          {filters.countries.includes(country.code) && (
                            <Check className="w-4 h-4 text-primary" />
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-foreground-secondary text-center">
                        {t('admin.configuration.shipping.search.noCountryFound')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pays sélectionnés (chips modernisés) */}
              {selectedCountries.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedCountries.map(country => (
                    <div
                      key={country.code}
                      className="inline-flex items-center px-2.5 py-1 bg-primary text-white text-xs rounded-lg font-medium shadow-sm"
                    >
                      <span className="truncate max-w-[80px]">{country.name}</span>
                      <button
                        onClick={() => handleToggleCountry(country.code)}
                        className="ml-1.5 hover:bg-primary-dark rounded transition-colors p-0.5 flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions modernisées */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-8 pt-6 border-t border-border gap-4">
            <div className="text-sm text-foreground-secondary font-medium">
              {t('admin.configuration.shipping.search.methodsFound', { count: filteredMethods.length })}
            </div>
            <button
              onClick={resetFilters}
              className="flex items-center px-5 py-2.5 text-sm font-medium text-foreground-secondary bg-background-tertiary hover:bg-border rounded-xl transition-all whitespace-nowrap"
            >
              <X className="h-4 w-4 mr-2" />
              {t('admin.configuration.shipping.search.resetFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* Liste des méthodes modernisée */}
      <div className="bg-surface rounded-2xl shadow-sm p-4 sm:p-6 min-w-0 overflow-x-hidden">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">{t('admin.configuration.shipping.search.title')}</h3>
          <span className="px-4 py-2 bg-primary-10 text-primary-dark rounded-xl text-sm font-semibold">
            {t('admin.configuration.shipping.search.results', { count: filteredMethods.length })}
          </span>
        </div>

        {showLoader ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-14 w-14 border-4 border-primary border-t-transparent mx-auto mb-6"></div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {contextInitialLoading
                ? t('admin.configuration.shipping.search.loading')
                : t('admin.configuration.shipping.search.searching')
              }
            </h3>
            <p className="text-foreground-secondary text-sm">
              {t('admin.configuration.shipping.search.pleaseWait')}
            </p>
          </div>
        ) : loadError ? (
          <div className="text-center py-16 bg-error-10 rounded-2xl border border-error/30">
            <AlertTriangle className="h-16 w-16 text-error mx-auto mb-5" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('admin.configuration.shipping.search.loadErrorTitle')}
            </h3>
            <p className="text-error text-sm max-w-lg mx-auto px-4">{loadError}</p>
            <button
              type="button"
              onClick={() => {
                setSendCloudLoading(true);
                setLoadError(null);
                const load =
                  catalogMode === 'merchant' && organizationId
                    ? ShippingService.browseSendcloudMethodsMerchant(organizationId)
                    : ShippingService.browseSendcloudMethods();
                load
                  .then(methods => {
                    setAllMethods(Array.isArray(methods) ? methods : []);
                    setLoadError(null);
                  })
                  .catch(err => {
                    const message = err instanceof Error ? err.message : String(err);
                    setLoadError(message || t('admin.configuration.shipping.search.loadError'));
                  })
                  .finally(() => setSendCloudLoading(false));
              }}
              className="mt-6 inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary-dark shadow-md transition-all"
            >
              {t('admin.configuration.shipping.search.retry')}
            </button>
          </div>
        ) : filteredMethods.length === 0 ? (
          <div className="text-center py-16 bg-background-tertiary rounded-2xl border-2 border-dashed border-border">
            <Package className="h-16 w-16 text-foreground-secondary mx-auto mb-5" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('admin.configuration.shipping.search.noMethodsFound')}
            </h3>
            <p className="text-foreground-secondary mb-6 text-sm">
              {t('admin.configuration.shipping.search.modifyCriteria')}
            </p>
            <button
              onClick={resetFilters}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary-dark shadow-md transition-all"
            >
              <X className="h-4 w-4 mr-2" />
              {t('admin.configuration.shipping.search.resetFiltersBtn')}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedMethods.map(method => {
              const isMethodSelected = selectedMethodIds.includes(method.id);
              const getMethodMinPrice = () => {
                if (!method.countries || method.countries.length === 0) return 0;
                return Math.min(...method.countries.map(c => applySendcloudMarkup(c.price)));
              };

              return (
                <div
                  key={method.id}
                  className="bg-background-tertiary rounded-xl p-4 sm:p-5 hover:bg-border-light transition-all border border-transparent hover:border-border min-w-0"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                        <h4 className="font-semibold text-foreground text-sm sm:text-base break-words">{method.name}</h4>
                        <span className="px-2.5 py-1 bg-surface text-foreground-secondary text-xs font-medium rounded-lg shrink-0">
                          {method.carrier}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-sm">
                        <span className="text-foreground-secondary font-medium">{method.minWeight}-{method.maxWeight}kg</span>
                        <span className="font-bold text-success text-base">{t('admin.configuration.shipping.search.from')} {getMethodMinPrice()}€</span>
                        <span className="flex items-center text-foreground-secondary">
                          <MapPin className="w-4 h-4 mr-1.5" />
                          {t('admin.configuration.shipping.search.countriesCount', { count: method.countries?.length || 0 })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:ml-4 shrink-0 self-end sm:self-center">
                      <button
                        onClick={() => setExpandedMethod(expandedMethod === method.id ? null : method.id)}
                        className="p-2.5 hover:bg-surface rounded-xl transition-all"
                      >
                        {expandedMethod === method.id ? (
                          <ChevronUp className="w-5 h-5 text-foreground-secondary" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-foreground-secondary" />
                        )}
                      </button>
                      {isMethodSelected ? (
                        <span
                          className="flex items-center px-5 py-2.5 text-sm font-semibold rounded-xl bg-surface border border-border text-foreground-secondary cursor-default"
                          title={t('admin.configuration.shipping.search.alreadyAddedHint', 'Retirez la méthode depuis la liste des méthodes actives')}
                        >
                          <Check className="w-4 h-4 mr-2 text-success" />
                          {t('admin.configuration.shipping.search.alreadyAdded', 'Ajouté')}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openCustomizationModal(method as SendCloudShippingMethod)}
                          className="flex items-center px-5 py-2.5 text-sm font-semibold rounded-xl transition-all shadow-sm bg-primary text-white hover:bg-primary-dark"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          {t('admin.configuration.shipping.search.add')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Détails étendus modernisés */}
                  {expandedMethod === method.id && (
                    <div className="mt-5 pt-5 border-t border-border">
                      <h5 className="font-semibold text-foreground mb-4 text-sm">
                        {t('admin.configuration.shipping.search.countriesAndPrices', { count: method.countries?.length || 0 })}
                      </h5>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {method.countries?.map((country) => {
                          const ccode = sendcloudCountryCode(country as { code?: string; iso_2?: string });
                          const priceWithMarkup = applySendcloudMarkup(country.price);
                          return (
                            <div key={`${method.id}-${ccode || 'x'}`} className="flex items-center justify-between p-3 bg-surface rounded-lg border border-border">
                              <span className="text-sm text-foreground font-medium">{country.name}</span>
                              <span className="text-sm font-bold text-foreground">{priceWithMarkup}€</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination modernisée */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                <div className="text-sm text-foreground-secondary font-medium">
                  {t('admin.configuration.shipping.search.page', { current: currentPage, total: totalPages })}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-5 py-2.5 text-sm font-medium bg-background-tertiary text-foreground-secondary rounded-xl hover:bg-border disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.configuration.shipping.search.previous')}
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-5 py-2.5 text-sm font-medium bg-background-tertiary text-foreground-secondary rounded-xl hover:bg-border disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.configuration.shipping.search.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Modal de personnalisation */}
      {isCustomizationModalOpen && customizationModal.method && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-3 py-6 sm:px-6 sm:py-10 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="carrier-customization-title"
        >
          <div className="w-full max-w-[min(80rem,96vw)] max-h-[min(92vh,900px)] bg-surface rounded-2xl shadow-2xl flex flex-col min-h-0 overflow-hidden border border-border">
            <div className="p-5 sm:p-7 space-y-5 overflow-y-auto flex-1 min-h-0 [scrollbar-gutter:stable]">
            <div>
              <h3 id="carrier-customization-title" className="text-lg sm:text-xl font-semibold text-foreground">
                {t('admin.configuration.shipping.customization.title', { carrier: customizationModal.method.carrier })}
              </h3>
              <p className="text-sm text-foreground-secondary mt-1">
                {t('admin.configuration.shipping.customization.description')}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('admin.configuration.shipping.customization.customNameLabel')}
              </label>
              <input
                type="text"
                value={customizationModal.customName}
                onChange={(e) => setCustomizationModal(prev => ({ ...prev, customName: e.target.value }))}
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm text-foreground bg-surface"
                placeholder={t('admin.configuration.shipping.customization.customNamePlaceholder')}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t('admin.configuration.shipping.customization.countryPricesLabel')}
                  </p>
                  <p className="text-xs text-foreground-secondary">
                    {t('admin.configuration.shipping.customization.countryPricesHelper')}
                  </p>
                </div>
                <span className="text-xs font-semibold px-2 py-1 bg-background-tertiary rounded-full text-foreground-secondary">
                  {customizationModal.method.countries?.length || 0} {t('admin.configuration.shipping.customization.countriesCount')}
                </span>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={selectAllModalCountries}
                  disabled={!customizationModal.method?.countries?.length}
                  className="px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-xl hover:bg-primary-dark disabled:opacity-50 transition-all shadow-sm"
                >
                  {t('admin.configuration.shipping.selectAll')}
                </button>
                <button
                  type="button"
                  onClick={deselectAllModalCountries}
                  disabled={customizationModal.selectedCountries.length === 0}
                  className="px-3 py-1.5 text-xs font-medium bg-border-medium text-foreground rounded-xl hover:bg-border disabled:opacity-50 transition-all"
                >
                  {t('admin.configuration.shipping.deselectAll')}
                </button>
              </div>

              <div className="min-h-[200px] max-h-[min(52vh,560px)] overflow-y-auto pr-1 sm:pr-2 border border-border/60 rounded-xl bg-background-tertiary/30 p-2 sm:p-3">
                {customizationModal.method.countries && customizationModal.method.countries.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {customizationModal.method.countries.map(country => {
                      const ccode = sendcloudCountryCode(country as { code?: string; iso_2?: string });
                      if (!ccode) return null;
                      const isSelected = customizationModal.selectedCountries.includes(ccode);
                      const defaultPriceWithMarkup = applySendcloudMarkup(country.price);
                    return (
                        <div
                          key={`${customizationModal.method?.id}-${ccode}`}
                          className={`flex flex-col gap-3 p-3 rounded-xl border transition-all ${isSelected ? 'border-primary bg-primary-10' : 'border-border bg-background-tertiary'}`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleModalCountry(ccode)}
                              className="mt-1 h-4 w-4 text-primary border-border-medium rounded focus:ring-primary"
                            />
                            <div>
                              <p className="text-sm font-semibold text-foreground">
                                {country.name} <span className="text-foreground-secondary">({ccode})</span>
                              </p>
                              <p className="text-xs text-foreground-secondary">
                                {t('admin.configuration.shipping.customization.defaultPrice', { price: defaultPriceWithMarkup })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={customizationModal.countryPrices[ccode] ?? 0}
                              onChange={(e) => handlePriceChange(ccode, e.target.value)}
                              disabled={!isSelected}
                              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm disabled:opacity-50 text-foreground bg-surface"
                            />
                            <span className="text-sm font-medium text-foreground-secondary">€</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-foreground-secondary text-center py-6 border border-dashed border-border rounded-xl">
                    {t('admin.configuration.shipping.customization.noCountries')}
                  </div>
                )}
              </div>
            </div>

            {modalError && (
              <div className="text-sm text-error bg-error-10 border border-error-20 rounded-xl px-4 py-2">
                {modalError}
              </div>
            )}
            </div>

            <div className="flex-shrink-0 flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-5 border-t border-border bg-background-secondary">
              <button
                type="button"
                onClick={closeCustomizationModal}
                className="px-5 py-2.5 text-sm font-medium text-foreground-secondary bg-background-tertiary hover:bg-border rounded-xl transition-all"
              >
                {t('admin.configuration.shipping.customization.cancel')}
              </button>
              <button
                type="button"
                onClick={handleCustomizationSubmit}
                disabled={isCustomizationConfirmDisabled}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-xl transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('admin.configuration.shipping.customization.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierFinder;
